/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Card, CardActions, CardContent, IconButton, Popover, TextField, Theme, Typography } from '@mui/material';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import React, { useState } from 'react';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { SVGSprite } from '../svg-sprite';
import { CSVButton } from '../../shared/components/csv-button';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import TaskIcon from '@mui/icons-material/Task';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { CCSnackbar, CCSnackbarVariant } from '../../shared/components/cc-snackbar';
import { ChatEventTypes, ChatFileChunk, ChatMessage, SuggestedPrompt } from '../../models/chat';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { v4 as uuidv4 } from 'uuid';

const CSV_INFO = `
    Attach a CSV file to include its data in the conversation and use the suggested prompts
    The file will not be stored and will be deleted after the conversation ends`;

const AI_WELCOME_MESSAGE = 'Hello, welcome to AI Insights!';
const AI_TITLE = 'AI Insights';

const styles = (theme: Theme) =>
    createStyles({
        bubbleContainer: {
            display: 'flex',
            margin: '2em'
        },
        chatContainer: {
            width: 'calc(100vw - 2em)',
            margin: 'auto',
            padding: '1em',
            display: 'flex',
            flexDirection: 'column',
            height: '86vh',
            justifyContent: 'center',
        },
        formContainer: {
            marginTop: '1em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5em'
        },
        formInput: {
            flex: 1,
            maxHeight: '5.8em',
            '& .MuiOutlinedInput-root': {
                maxHeight: '5.8em'
            }
        },
        chatTitle: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '0.3em',

            '&.is-empty img': {
                transform: 'scale(2)',
                marginRight: '0.5em'
            },
        },
        chatButton: {
            borderRadius: '50%',
            padding: '1em',
            height: '4.5em',
            [theme.breakpoints.between('xs', 'sm')]: {
                transform: 'scale(0.8)'
            },
        },
        chatList: {
            background: theme.palette.background.default,
            border: '1px solid #ccc',
            padding: '1em',
            minHeight: '300px',
            flex: 1,
            overflowY: 'auto',

            '&.is-empty': {
                flex: 0
            },

            '& table': {
                borderSpacing: 0,
                borderCollapse: 'collapse',
                display: 'block',
                width: 'max-content',
                maxWidth: '100%',
                overflow: 'auto',
                fontVariant: 'tabular-nums'
            },

            '& tr': {
                backgroundColor: theme.palette.background.paper,
                borderTop: '1px solid #d1d9e0b3',

                '&:nth-child(2n)': {
                    backgroundColor: theme.palette.background.default,
                }
            },

            '& td, & th': {
                padding: '6px 13px',
                border: '1px solid #d1d9e0'
            }
        },
        chatUser: {
            fontWeight: 500,
            textAlign: 'right',

            '& p': {
                padding: '1em',
                margin: 0,
                border: '1px solid #ccc',
                display: 'inline-block',
                borderRadius: '20px',
                backgroundColor: theme.palette.background.paper
            }
        },
        uploadButton: {
            transform: 'scale(1.5)',
            margin: '0 1em',
            minWidth: 'auto',
        },
        sendButton: {},
        errorMessage: {
            fontWeight: 400,
            textAlign: 'center',
            margin: '1em'
        },
        snackbar: {
            top: '7em',
        },
        suggestedPromptsContainer: {
            display: 'flex',
            gap: '1em',
            padding: '1em 0'
        },
        csvInfo: {
            textAlign: 'right',
            whiteSpace: 'pre-wrap',
        }
    });

interface Props extends WithStyles<typeof styles> {
    chatbotAccess: {url: string, token: string, userId: string};
    chatbotMessages: ChatMessage[];
    suggestedPrompts: SuggestedPrompt[];
    onSendMessage: (message: ChatMessage) => void;
    className?: string;
    isBubbleView?: boolean;
}

const toBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });
};

const ChatBox: React.FC<Props> = (props) => {
    const {
        classes,
        chatbotAccess,
        chatbotMessages,
        suggestedPrompts,
        isBubbleView,
        onSendMessage
    } = props;

    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isReconnectFailed, setIsReconnectFailed] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [fileData, setFileData] = useState<File | undefined>(undefined);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarVariant, setSnackbarVariant] = useState<CCSnackbarVariant>('success');

    const showSnackbar = (snackbarMessage: string, snackbarVariant: CCSnackbarVariant) => {
        setSnackbarMessage(snackbarMessage);
        setSnackbarVariant(snackbarVariant);
        setSnackbarOpen(true);
    };

    const { url: socketUrl, token, userId } = chatbotAccess;

    const {
        sendJsonMessage,
        readyState,
    } = useWebSocket(socketUrl, {
        onOpen: () => console.info('Chatbot connected'),
        onClose: (event) => console.warn('Chatbot disconnected:', event.code, event.reason),
        onError: (error) => console.error('Chatbot error:', error),
        onMessage: (event) => {
            // Check if the received data is a Blob
            if (event.data instanceof Blob) {
                // Convert the Blob to text
                const reader = new FileReader();
                reader.onload = function() {
                    try {
                        const jsonMessage = JSON.parse(reader.result as string);
                        // Process your JSON message here
                        if (jsonMessage.event === ChatEventTypes.chatComplete) { 
                            console.log(jsonMessage);
                            onSendMessage({ role: 'assistant', content: jsonMessage.data.message });
                            setIsLoading(false);
                        } else if (jsonMessage.event === ChatEventTypes.chatError) {
                            showSnackbar(jsonMessage.data.error || 'An error occurred while processing your message.', 'error');
                        }
                    } catch (error) {
                        const errorMessage = `Error parsing JSON: ${error}`;
                        showSnackbar(errorMessage, 'error');
                    }
                };
                reader.readAsText(event.data);
            } else {
                // Data is already a string, parse directly
                try {
                    const jsonMessage = JSON.parse(event.data);
                    // Process your JSON message here
                    if (jsonMessage.event === ChatEventTypes.chatComplete) {
                        onSendMessage({ role: 'assistant', content: jsonMessage.data.message });
                        setIsLoading(false);
                    } else if (jsonMessage.event === ChatEventTypes.chatError) {
                        showSnackbar(jsonMessage.data.error || 'An error occurred while processing your message.', 'error');
                    }
                } catch (error) {
                    const errorMessage = `Error parsing JSON: ${error}`;
                    showSnackbar(errorMessage, 'error');
                }
            }
        },
        //Will attempt to reconnect on all close events, such as server shutting down
        shouldReconnect: () => true,
        reconnectAttempts: 5,
        onReconnectStop: (numAttempts) => {
            console.warn('Chatbot reconnection failed after', numAttempts, 'attempts');
            setIsReconnectFailed(true);
        },
        protocols: [userId, token]
    });

    const connectionStatus = {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];

    const sendMessage = async (inputMessage: string) => {
        onSendMessage({ role: 'user', content: inputMessage });
        setIsLoading(true);

        if (fileData) {
            const b64File = await toBase64(fileData) as string;
            sendBase64InChunks(b64File.replace('data:text/csv;base64,77u/', ''), fileData.name, inputMessage);
        } else {
            sendJsonMessage({
                chat_id: uuidv4(),
                event: ChatEventTypes.chatMessage,
                data: {
                    message: inputMessage,
                    user_id: userId
                }
            });
        }
        setInput('');
    };

    const sendBase64InChunks = (base64String: string, fileName: string, message: string) => {
        const CHUNK_SIZE = 16384; // 16KB chunks
        // const CHUNK_SIZE = 32768; // 32KB chunks
        const totalChunks = Math.ceil(base64String.length / CHUNK_SIZE);
        const fileId = Math.random().toString(36).substring(7); // Unique ID for reassembly
        const chatId = uuidv4();
    
        for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = start + CHUNK_SIZE;
            const chunk = base64String.slice(start, end);
            const isLast = i === totalChunks - 1;
            const data: ChatFileChunk = {
                user_id: userId,
                file_id: fileId,
                file_name: fileName,
                is_last: isLast,
                chunk
            };

            if (isLast) {
                data.message = message;
            }

            const payload = {
                chat_id: chatId,
                event: ChatEventTypes.fileChunk,
                data
            };

            // Send the chunk
            sendJsonMessage(payload);
        }
    };

    const usePrompt = (promptMessage: string) => {
        sendMessage(promptMessage);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setFileData(undefined);
    };

    const onCSVImport = (parsedCSVFileData: string[][], csvFile?: unknown) => {
        setFileData(csvFile as File);
    };

    const onClearImport = () => {
        setFileData(undefined);
    };

    const isSendMessageDisabled = readyState !== ReadyState.OPEN || !input.trim();

    const renderChatContent = () => {
        return (
            <div className={classes.chatContainer}>
                <Typography
                    className={`${classes.chatTitle} ${chatbotMessages.length ? '' : 'is-empty'}`}
                    variant="h4"
                >
                    <SVGSprite name="default" /> { chatbotMessages.length ? AI_TITLE : AI_WELCOME_MESSAGE }
                </Typography>
                <>
                    {
                        isReconnectFailed ?
                            <div className={classes.chatList}>
                                <Typography
                                    className={classes.errorMessage}
                                    variant="h3"
                                    style={{ color: 'red' }}
                                >
                                    Sorry,  Chat is currently {connectionStatus} and reconnect cannot be reached.
                                </Typography>
                                <Typography
                                    variant="h4"
                                    className={classes.errorMessage}
                                >
                                    Please reload the page.
                                </Typography>
                                <Typography
                                    variant="h4"
                                    className={classes.errorMessage}
                                >
                                    If the problem persists, please contact support at sayhello@crowdcomfort.com
                                </Typography>
                            </div>
                            : <div className={`${classes.chatList} ${chatbotMessages.length ? '' : 'is-empty'}`}>
                                {chatbotMessages.map((message: any, index: number) => (
                                    <div key={index} className={message.role === 'user' ? classes.chatUser : ''} style={{ margin: '0.5em 0' }}>
                                        <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
                                    </div>
                                ))}
                                {isLoading && <div><i>Thinking...</i></div>}
                            </div>
                    }

                    <div className={classes.formContainer}>
                        <TextField
                            className={classes.formInput}
                            label="Ask me anything..."
                            variant="outlined"
                            value={input}
                            multiline
                            maxRows={4}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && !isSendMessageDisabled && sendMessage(input)}
                        />
                        {
                            fileData ?
                                <TaskIcon
                                    color="secondary"
                                    className={classes.uploadButton}
                                />
                                : <CSVButton
                                    classes={{
                                        button: classes.uploadButton
                                    }}
                                    size="large"
                                    color="secondary"
                                    importData={true}
                                    onCSVImport={onCSVImport}
                                >
                                    <NoteAddIcon />
                                </CSVButton>
                        }
                    </div>
                    <div className={classes.formContainer}>
                        <Button
                            className={classes.sendButton}
                            variant="contained"
                            color="primary"
                            onClick={() => sendMessage(input)}
                            disabled={isSendMessageDisabled}
                        >
                            Send
                        </Button>
                        {
                            fileData ? (
                                <Typography variant="body2">
                                    Attached file: {fileData.name}
                                    <IconButton
                                        aria-label="clear"
                                        onClick={onClearImport}
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                </Typography>
                            ) : (
                                <Typography
                                    variant="body2"
                                    className={classes.csvInfo}
                                >
                                    {CSV_INFO}
                                </Typography>
                            )
                        }
                    </div>
                </>
                {
                    suggestedPrompts.length > 0 && fileData && (
                        <div className={classes.suggestedPromptsContainer}>
                            {
                                suggestedPrompts.map((prompt: SuggestedPrompt) => {
                                    return (
                                        <Card key={prompt.id} variant="outlined">
                                            <CardContent>
                                                <Typography variant="subtitle1">
                                                    {prompt.prompt}
                                                </Typography>
                                            </CardContent>
                                            <CardActions>
                                                <Button
                                                    disabled={isLoading || readyState !== ReadyState.OPEN}
                                                    size="small"
                                                    onClick={() => usePrompt(prompt.prompt)}
                                                >
                                                    Use prompt
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    );
                                }) 
                            }
                        </div>
                    )
                }
            </div>
        );
    };

    const open = Boolean(anchorEl);
    const id = open ? 'chat-popover' : undefined;

    return (
        <>
            {
                isBubbleView ? (
                    <div className={classes.bubbleContainer}>
                        <Button
                            className={classes.chatButton}
                            variant="contained"
                            aria-describedby={id}
                            aria-label="Open chat"
                            color="secondary"
                            onClick={handleClick}
                        >
                            {
                                open ? <CloseIcon />
                                    : <ChatIcon />
                            } 
                        </Button>
                        <Popover
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        >
                            {
                                renderChatContent()
                            }
                        </Popover>
                    </div>
                ) : renderChatContent()
            }
            <CCSnackbar
                className={classes.snackbar}
                variant={snackbarVariant}
                message={snackbarMessage}
                autoHideDuration={4000}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transition="right"
                open={snackbarOpen}
                onClose={() => setSnackbarOpen(false)}
            />
        </>
    );
};

const MUIComponent = withStyles(styles)(ChatBox);
export { MUIComponent as ChatBox };
